import { useMemo } from 'react';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Narrows menu categories down to the ones a bundle is built from, in the
 * order Contentful lists them. When the menu carries none of those IDs, the
 * categories whose name mentions plates are used instead.
 */
export const useBundleCategories = <Category extends MenuCategory>(params: Params<Category>) => {
  const { categories, categoryIds } = params;

  return useMemo(() => {
    if (!categories) {
      return [];
    }

    const requestedCategories = pickCategoriesById(categories, categoryIds);

    if (requestedCategories.length > 0) {
      return requestedCategories;
    }

    return categories.filter(checkIfFallbackCategory);
  }, [categories, categoryIds]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function pickCategoriesById<Category extends MenuCategory>(
  categories: readonly Category[],
  categoryIds: readonly string[],
) {
  const categoriesById = new Map(categories.map((category) => [category.id, category]));

  return categoryIds
    .map((categoryId) => categoriesById.get(categoryId))
    .filter((category) => category !== undefined);
}

function checkIfFallbackCategory(category: MenuCategory) {
  return category.name.toLowerCase().includes(FALLBACK_CATEGORY_KEYWORD);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const FALLBACK_CATEGORY_KEYWORD = 'plates';

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuCategory = {
  id: string;
  name: string;
};

type Params<Category> = {
  categories: readonly Category[] | undefined;
  categoryIds: readonly string[];
};
